import type { AbstractControl } from '@angular/forms';
import type { TranslateService } from '@ngx-translate/core';

/**
 * Returns a translated validation error message for the given form control.
 * Returns `null` if the control has no visible errors (not touched or no errors).
 *
 * Supports: required, email, pattern, minlength, maxlength, min, max,
 * and a generic fallback for any other error.
 */
export function getFieldError(control: AbstractControl | null | undefined, translate: TranslateService): string | null {
  if (!control || !control.touched || !control.errors) return null;

  if (control.errors['requiredPrimaryLanguage']) {
    const language = control.errors['requiredPrimaryLanguage'].language ?? 'en';
    const localisedLanguage = translateLanguageLabel(language, translate);
    return translate.instant('errors.requiredPrimaryLanguage', { language: localisedLanguage });
  }
  if (control.errors['requiredDefaultLanguage']) {
    const language = control.errors['requiredDefaultLanguage'].language ?? 'en';
    const localisedLanguage = translateLanguageLabel(language, translate);
    return translate.instant('errors.requiredDefaultLanguage', { language: localisedLanguage });
  }
  if (control.errors['required']) return translate.instant('errors.required');
  if (control.errors['email']) return translate.instant('errors.invalidEmail');
  if (control.errors['pattern']) return translate.instant('errors.pattern');
  if (control.errors['minlength']) {
    const min = control.errors['minlength'].requiredLength;
    return translate.instant('errors.minLength', { min });
  }
  if (control.errors['maxlength']) {
    const max = control.errors['maxlength'].requiredLength;
    return translate.instant('errors.maxLength', { max });
  }
  if (control.errors['min']) {
    const min = control.errors['min'].min;
    return translate.instant('errors.min', { min });
  }
  if (control.errors['max']) {
    const max = control.errors['max'].max;
    return translate.instant('errors.max', { max });
  }
  if (control.errors['conflict']) return translate.instant('errors.conflict');

  return translate.instant('errors.invalid');
}

/**
 * Translate a {@link SupportedLanguage} code into the user-facing language
 * name (`'en' → 'English'`, etc.) using the `translatableInput.languageTab.*`
 * translation keys. Falls back to the upper-cased code if no translation is
 * registered.
 */
function translateLanguageLabel(language: string, translate: TranslateService): string {
  const key = `translatableInput.languageTab.${language}`;
  const translated = translate.instant(key);
  if (translated && translated !== key) return translated;
  return language.toUpperCase();
}
